// Auth store using Svelte 5 runes
// Discord OAuth is the only sign-in path (see ADR-0005).

import {
  auth,
  signInAnonymously,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithCustomToken,
  signOut,
} from '../services/firebase.js';
import { API_ENDPOINTS } from '../services/endpoints.js';
import { apiFetch } from '../services/api-fetch.js';
import { DISCORD_BOT_CLIENT_ID } from '../logic/config.js';
import {
  storeDiscordUsername,
  loadDiscordUsername,
  clearDiscordUsername,
} from '../logic/profile.js';
import { fetchMyProfile } from '../services/profile-api.js';

// --- Reactive auth state ---
export const authState = $state({
  /** @type {{ uid: string, isAnonymous: boolean, displayName: string|null, email: string|null, photoURL: string|null } | null} */
  user: null,
  /** @type {boolean} */
  ready: false,
  /** @type {boolean} */
  initializing: false,
  /**
   * The signed-in user's Discord username slug (the want-page URL handle), or
   * null when anonymous/unknown. Cached locally so the sidebar can link straight
   * to /u/@<username>/wantlist instead of the generic @me. Kept in sync by the
   * auth listener below (set from OAuth, restored from storage, cleared on
   * sign-out).
   * @type {string|null}
   */
  discordUsername: null,
});

let usernameFetchInFlight = false;

/**
 * Keep authState.discordUsername correct for the given Firebase user.
 *
 * Anonymous/signed-out -> cleared. Signed-in -> the cached slug if present
 * (instant), otherwise resolved from the server ONCE and cached. The server
 * resolve is what makes a pre-existing session (signed in before username
 * caching shipped) link straight to /u/@<username>/wantlist instead of @me,
 * without forcing a re-sign-in. Fire-and-forget: reactive state updates the
 * sidebar when it lands. Best-effort — a failed fetch leaves the @me fallback.
 */
async function ensureDiscordUsername(user) {
  if (!user || user.isAnonymous) {
    clearDiscordUsername();
    authState.discordUsername = null;
    return;
  }
  const cached = loadDiscordUsername();
  if (cached) {
    authState.discordUsername = cached;
    return;
  }
  if (usernameFetchInFlight) return;
  usernameFetchInFlight = true;
  try {
    const token = await user.getIdToken().catch(() => null);
    const profile = await fetchMyProfile(token);
    if (profile?.discordUsername) {
      storeDiscordUsername(profile.discordUsername);
      authState.discordUsername = profile.discordUsername;
    }
  } finally {
    usernameFetchInFlight = false;
  }
}

// The one auth init promise (initializeAuth is idempotent)
let authInitPromise = null;

/**
 * Normalize a Firebase user object to a plain object.
 */
function normalizeUser(firebaseUser) {
  if (!firebaseUser) return null;
  return {
    uid: firebaseUser.uid,
    isAnonymous: firebaseUser.isAnonymous,
    displayName: firebaseUser.displayName,
    email: firebaseUser.email,
    photoURL: firebaseUser.photoURL,
  };
}

/**
 * Initialize auth: set persistence, check existing user, sign in anonymously if needed.
 * Safe to call multiple times — returns cached promise.
 */
export function initializeAuth() {
  if (authInitPromise) return authInitPromise;

  authState.initializing = true;

  authInitPromise = new Promise((resolve, reject) => {
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('[Auth] Persistence set to LOCAL');

        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe(); // Only need first check here

          if (user) {
            console.log('[Auth] User already authenticated:', user.uid);
            authState.user = normalizeUser(user);
            authState.ready = true;
            authState.initializing = false;
            resolve(user);
          } else {
            console.log('[Auth] Signing in anonymously...');
            signInAnonymously(auth)
              .then((cred) => {
                console.log('[Auth] Anonymous sign-in successful:', cred.user.uid);
                authState.user = normalizeUser(cred.user);
                authState.ready = true;
                authState.initializing = false;
                resolve(cred.user);
              })
              .catch((error) => {
                console.error('[Auth] Anonymous sign-in failed:', error);
                authState.initializing = false;
                reject(error);
              });
          }
        });
      })
      .catch((error) => {
        console.error('[Auth] Failed to set persistence:', error);
        authState.initializing = false;
        reject(error);
      });
  });

  return authInitPromise;
}

/**
 * Is the Viewer signed in (not anonymous)? Decided on the restored session:
 * awaits auth initialization with no timeout (the same wait getIdToken does
 * for every request), so a slow restore never reads as anonymous. A failed
 * init is not signed in.
 * @returns {Promise<boolean>}
 */
export async function isSignedIn() {
  await initializeAuth().catch(() => {});
  return !isUserAnonymous();
}

/**
 * Get current user's UID. Waits for auth init if needed.
 */
export async function getUserToken() {
  if (!auth.currentUser) {
    console.log('[Auth] getUserToken called before auth ready, waiting...');
    await initializeAuth();
  }
  const user = auth.currentUser;
  if (!user) {
    console.error('[Auth] getUserToken: Still no user after auth init');
    throw new Error('Failed to authenticate - please refresh the page');
  }
  return user.uid;
}

/**
 * Sign out, then sign in anonymously for continued app usage.
 */
export async function signOutUser() {
  try {
    console.log('[Auth] Signing out user...');
    clearDiscordUsername();
    authState.discordUsername = null;
    await signOut(auth);
    console.log('[Auth] Signing in anonymously after sign out...');
    await signInAnonymously(auth);
    console.log('[Auth] Sign out complete, now anonymous');
    return { success: true };
  } catch (error) {
    console.error('[Auth] Sign out failed:', error);
    throw error;
  }
}

/**
 * Get the current user's Firebase ID token (JWT) for Authorization headers.
 * Awaits auth initialization first so early callers (e.g. search's first paint)
 * don't fire an unauthenticated request and 401 (review §9a). Returns null only
 * if there is genuinely no user after init.
 */
export async function getIdToken() {
  if (!auth.currentUser) {
    // Auth may still be initializing on first paint; wait for it. initializeAuth
    // is idempotent (returns the cached promise), so this resolves once auth is
    // ready (anonymous or signed-in) rather than returning a premature null.
    await initializeAuth().catch(() => {});
  }
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

// --- Discord OAuth ---

const DISCORD_OAUTH_STATE_KEY = 'discord_oauth_state';

/**
 * Initiate Discord OAuth2 flow by redirecting to Discord's authorize URL.
 * Stores CSRF state + return path in sessionStorage.
 * @param {string} [returnPath] - Path to return to after sign-in (defaults to current path)
 */
export function initiateDiscordOAuth(returnPath) {
  const state = crypto.randomUUID();
  const storeReturnPath = returnPath || window.location.pathname + window.location.search;

  sessionStorage.setItem(
    DISCORD_OAUTH_STATE_KEY,
    JSON.stringify({ state, returnPath: storeReturnPath })
  );

  const redirectUri = `${window.location.origin}/auth/discord/callback`;
  const params = new URLSearchParams({
    client_id: DISCORD_BOT_CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: 'identify guilds',
    state,
  });

  console.log('[Auth] Redirecting to Discord OAuth...');
  window.location.href = `https://discord.com/oauth2/authorize?${params.toString()}`;
}

/**
 * Complete Discord OAuth2 flow: verify state, exchange code, sign in.
 *
 * If the caller is currently signed in anonymously, the anonymous ID token is
 * forwarded so the server can migrate `claimed_binders` and `group_members`
 * from the anonymous UID to `discord_{discord_id}` before the sign-in
 * completes (see ADR-0005).
 *
 * @param {string} code - Authorization code from Discord
 * @param {string} state - State parameter from callback URL
 * @returns {Promise<{success: boolean, returnPath: string}>}
 */
export async function completeDiscordOAuth(code, state) {
  // Verify CSRF state
  const stored = sessionStorage.getItem(DISCORD_OAUTH_STATE_KEY);
  if (!stored) {
    throw new Error('No OAuth state found. Please try signing in again.');
  }

  const { state: expectedState, returnPath } = JSON.parse(stored);
  if (state !== expectedState) {
    sessionStorage.removeItem(DISCORD_OAUTH_STATE_KEY);
    throw new Error('OAuth state mismatch. Please try signing in again.');
  }

  sessionStorage.removeItem(DISCORD_OAUTH_STATE_KEY);

  // Capture anonymous caller's ID token so the server can migrate their data.
  // Wait for auth init — after the OAuth redirect Firebase restores the
  // anonymous session from browserLocalPersistence asynchronously, and
  // `auth.currentUser` is null until that completes. Without the await we
  // race the restore and silently skip the migration.
  // If anything fails here (no user, token error), we proceed without
  // migration rather than blocking sign-in.
  /** @type {string|null} */
  let anonIdToken = null;
  try {
    await initializeAuth();
    const currentUser = auth.currentUser;
    if (currentUser?.isAnonymous) {
      anonIdToken = await currentUser.getIdToken();
    }
  } catch (e) {
    console.warn('[Auth] Failed to capture anonymous ID token for migration:', e);
  }

  // Exchange code for Firebase custom token. Only an ANONYMOUS token is sent
  // (the migration source), so the token is passed explicitly.
  const redirectUri = `${window.location.origin}/auth/discord/callback`;
  const res = await apiFetch(
    API_ENDPOINTS.discord_oauth,
    { method: 'POST', json: { code, redirect_uri: redirectUri } },
    { token: anonIdToken }
  );

  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error || 'Discord sign-in failed');
  }

  // Cache the Discord username slug (the want-page URL handle) BEFORE sign-in,
  // so the auth listener that fires on sign-in restores it into reactive state.
  // Also set it directly for immediacy.
  storeDiscordUsername(data.username);
  authState.discordUsername = data.username || null;

  // Sign in with the custom token
  await signInWithCustomToken(auth, data.customToken);
  console.log('[Auth] Discord OAuth sign-in successful:', data.displayName);

  return { success: true, returnPath: returnPath || '/' };
}

// --- Convenience getters ---

export function isUserAnonymous() {
  return authState.user?.isAnonymous ?? true;
}

export function getCurrentUser() {
  return authState.user;
}

// --- Trade session token helpers (iOS recovery) ---

export function storeSessionForTrade(tradeId, userToken) {
  try {
    localStorage.setItem(`trade_session_${tradeId}`, userToken);
    console.log('[Auth] Stored session token for trade:', tradeId);
  } catch (e) {
    console.warn('[Auth] Failed to store session token:', e);
  }
}

export function getStoredSessionForTrade(tradeId) {
  try {
    const token = localStorage.getItem(`trade_session_${tradeId}`);
    if (token) {
      console.log('[Auth] Found stored session token for trade:', tradeId);
    }
    return token;
  } catch (e) {
    console.warn('[Auth] Failed to retrieve session token:', e);
    return null;
  }
}

// --- Persistent auth listener ---
// Keeps authState.user in sync with Firebase auth at all times.
if (typeof window !== 'undefined') {
  onAuthStateChanged(auth, (user) => {
    authState.user = normalizeUser(user);
    ensureDiscordUsername(user);
    console.log(
      '[Auth] State changed:',
      user ? `${user.uid} (anonymous=${user.isAnonymous})` : 'signed out'
    );
  });
}
